import { useEffect, useRef } from 'react';

/**
 * The foot of a list that grows as it is scrolled.
 *
 * **It asks for the next batch before the bottom arrives.** The sentinel is
 * watched with a root margin of most of a screen, so the request goes out while
 * the reader is still a screenful away and the rows are usually there before
 * they are reached. What they see is one continuous list.
 *
 * **It is the same height whether or not a batch is in flight**, so appending
 * never moves what is already on screen: the message inside changes, the box
 * does not. A spinner that appears and disappears would shift the rows above it
 * on every batch, which is the visible page boundary this exists to avoid.
 *
 * At the end it says what the list holds, so "no more" is stated rather than
 * inferred from scrolling and nothing happening.
 */
export function ListEnd({
  loadMore,
  hasMore,
  appending,
  loaded,
  total,
  noun,
}: {
  loadMore: () => void;
  hasMore: boolean;
  appending: boolean;
  loaded: number;
  total: number;
  noun: string;
}) {
  const sentinel = useRef<HTMLDivElement>(null);
  // The callback, without re-observing on every render it changes in.
  const ask = useRef(loadMore);
  ask.current = loadMore;

  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) ask.current(); },
      // a screenful of warning, so the batch is usually loaded before it is read
      { rootMargin: '800px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore]);

  return (
    <div
      ref={sentinel}
      aria-live="polite"
      className="flex min-h-[4.5rem] items-center justify-center px-4 text-sm text-neutral-500 tabular-nums"
    >
      {hasMore
        ? appending
          ? `Loading more ${noun}…`
          : `${loaded.toLocaleString()} of ${total.toLocaleString()} ${noun}`
        : total > 0
          ? `All ${total.toLocaleString()} ${noun}`
          : null}
    </div>
  );
}
